import type { ReactNode } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';

import { getAnonClient } from '@boc/framework/client';
import type { ProductTypesQuery, ProductTypesQueryVariables } from '@boc/framework/saleor-graphql';
import { ProductTypesDocument } from '@boc/framework/saleor-graphql';
import { Dialog, Transition } from '@headlessui/react';
import {
  ArrowUpOnSquareStackIcon,
  ChartBarIcon,
  ClockIcon,
  PhotoIcon,
  PuzzlePieceIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';
import { motion, transform, useMotionValueEvent, useScroll } from 'framer-motion';
import Pluralize from 'pluralize';
import type { HeadersFunction, LinksFunction, MetaDescriptor, Params } from 'react-router';
import { data, Link, useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import { SALEOR } from '~/framework.server/saleor';
import { createSignedUrl } from '~/lib/server/s3';
import { buttonVariants } from '~/local/components/button';
import FAQ from '~/local/components/faq';
import { SLUG_PUZZLE } from '~/local/custom-puzzle';
import { CustomPuzzleFlowSheet } from '~/local/custom-puzzle/components/custom-puzzle-flow-sheet';
import { DispatchNoticeDialog } from '~/local/custom-puzzle/components/dispatch-notice-dialog';
import { MapPuzzleFlowSheet } from '~/local/custom-puzzle/components/map-puzzle-flow-sheet';
import image1 from '~/local/custom-puzzle/images/custom-puzzle-1.png';
import image2 from '~/local/custom-puzzle/images/custom-puzzle-2.png';
import heroCP from '~/local/custom-puzzle/images/hero-banner-custom-puzzle.png';
import heroLP from '~/local/custom-puzzle/images/hero-banner-lp.png';
import mapPuzzleExampleAE from '~/local/custom-puzzle/images/map-puzzle-example-ae.jpg';
import mapPuzzleExampleIntl from '~/local/custom-puzzle/images/map-puzzle-example-intl.jpg';
import mapPuzzleExample from '~/local/custom-puzzle/images/map-puzzle-example.jpg';
import Hero from '~/local/homepage/components/hero';
import { SectionWithHeading } from '~/local/homepage/components/sections';
import env from '~/secrets.server';
// import { Slider } from '~/local/homepage/components/slider';
import { cn } from '~/styling';
import { gradient as gradientStyle } from '~/styling/gradients';
import { canonicalize, imgUrl, parseParams, urls } from '~/urls';

import type { Route } from './+types/landing';

export const links: LinksFunction = () => [];

export const handle = {
  header: {
    bgColour: 'bg-primary-2-10',
  },
  bgColour: 'bg-primary-2-10',
};

const schemaParams = z.object({
  slug: z.string().optional(),
});

export const loader = async ({ params }: Route.LoaderArgs) => {
  const { saleorLocale, channel } = parseParams(params);

  const client = getAnonClient(SALEOR);
  const responseProductTypes = await client.request<ProductTypesQuery, ProductTypesQueryVariables>(
    ProductTypesDocument,
    {
      filter: {
        slugs: [SLUG_PUZZLE],
      },
      channel: channel.slug,
      locale: saleorLocale,
    }
  );

  const productType = responseProductTypes.productTypes?.edges[0].node;
  const products = productType?.products?.edges.map((edge) => edge.node);

  const instock = products?.some((p) => p.isAvailable);

  if (!productType || !products) {
    throw new Response(null, {
      status: 404,
      statusText: 'Custom Puzzle ProductType or Products Not Found',
    });
  }

  // Generate S3 upload URLs for the custom puzzle flow
  const today = new Date();
  const uuid = uuidv4();
  const datePath = today.toISOString().split('T')[0].replace(/-/g, '/');

  const originalFilename = `${env.NODE_ENV}/custom-puzzle/${datePath}/original/${uuid}`;
  const croppedFilename = `${env.NODE_ENV}/custom-puzzle/${datePath}/cropped/${uuid}`;

  const originalUploadUrl = await createSignedUrl({
    bucket: env.UPLOADS_BUCKET_NAME,
    key: originalFilename,
    expiry: 60 * 10,
  });

  const croppedUploadUrl = await createSignedUrl({
    bucket: env.UPLOADS_BUCKET_NAME,
    key: croppedFilename,
    expiry: 60 * 10,
  });

  return data(
    {
      products,
      instock,
      originalUploadUrl,
      croppedUploadUrl,
    },
    {
      // headers: CacheControlOneMonth,
    }
  );
};

// export const headers: HeadersFunction = ({ loaderHeaders }: Route.HeadersArgs) => {
//   const loaderCacheHeader = loaderHeaders.get('Cache-Control');

//   if (loaderCacheHeader) {
//     return { 'Cache-Control': loaderCacheHeader };
//   }

//   return CacheControlOneMonth;
// };

export const meta: Route.MetaFunction = ({ location }) => {
  const canonical: MetaDescriptor = {
    tagName: 'link',
    rel: 'canonical',
    href: canonicalize(location),
  };

  return [
    canonical,
    {
      title: `Craft Your Unique Story with Custom Photo Puzzles only at Jigsaw Nation`,
    },
    {
      description:
        'Transform your cherished memories into interactive works of art with our personalized photo puzzles. Design your own puzzle by uploading your favorite images, and choose from various sizes for a unique and meaningful experience. Surprise your loved ones with a custom puzzle that captures the essence of your special moments. Create lasting memories, one puzzle piece at a time. Start crafting your personalized masterpiece today!',
    },
  ];
};

function Slide({
  name,
  startHref,
}: {
  params: Params;
  index: number;
  name: string;
  startHref: string;
}) {
  return (
    <div className="flex w-full flex-col items-center lg:flex-row">
      <div className="relative h-[200px] w-full overflow-clip sm:h-[300px] md:h-[400px] lg:h-[650px]">
        <img
          className="absolute -right-[10%] bottom-4 w-2/3 rotate-6 drop-shadow-lg lg:top-0 lg:right-5 lg:bottom-auto lg:w-4/5"
          alt={`Create your own personalized ${name
            .toLowerCase()
            .replace('puzzle', 'photo puzzle')}`}
          loading="eager"
          src={imgUrl(image2, { width: 550 })}
          srcSet={`${imgUrl(image2, { width: 550 })} 550w, ${imgUrl(image2, { width: 260 })} 260w`}
          sizes="(min-width: 1024) 550w, 260w"
        />
        <img
          className="absolute top-0 left-0 w-2/3 drop-shadow-lg lg:top-auto lg:bottom-0 lg:w-4/5"
          alt={`Create your own personalized ${name
            .toLowerCase()
            .replace('puzzle', 'photo puzzle')}`}
          loading="eager"
          src={imgUrl(image1, { width: 550 })}
          srcSet={`${imgUrl(image1, { width: 550 })} 550w, ${imgUrl(image1, { width: 260 })} 260w`}
          sizes="(min-width: 1024) 550w, 260w"
        />
      </div>
      <div className="flex flex-col gap-y-4 px-8 pt-2 pb-6 md:pt-4 lg:py-0">
        <h1 className="font-heading-condensed text-primary-2-80 text-xl leading-7 font-medium tracking-tight sm:text-4xl sm:leading-9">
          Create your own personalized {name.toLowerCase().replace('puzzle', 'photo puzzle')}
        </h1>
        <p className="text-neutral-2">
          Turn your cherished memories into custom{' '}
          {Pluralize(name.toLowerCase().replace('puzzle', 'jigsaw puzzle'), 2)} - crafted by you,
          enjoyed by everyone.
        </p>
        <Link to={startHref} className={cn(buttonVariants())}>
          Start Designing
        </Link>
      </div>
    </div>
  );
}

const Steps = ({
  name,
  steps,
}: {
  name: string;
  steps: { icon: ReactNode; description: string }[];
}) => {
  const refStepsBox = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: refStepsBox,
    offset: ['center end', 'end 0.6'],
  });
  const [gradientPercent, setGradientPercent] = useState(0);
  const [enabledStep, setEnabledStep] = useState(0);

  useMotionValueEvent(scrollYProgress, 'change', (latest) => {
    setGradientPercent(transform(latest, [0.1, 1], [10, 100]));
    setEnabledStep(transform(latest, [0.2, 0.8], [0, steps.length - 1]));
  });

  return (
    <>
      <div className="mt-14">
        <SectionWithHeading heading={`Design your personalized ${name.toLowerCase()} in minutes`}>
          <div ref={refStepsBox} className="text-primary-2-80 relative mt-20 lg:w-4/5">
            <motion.div
              className="absolute top-10 left-1/2 -z-10 hidden h-0.5 w-2/3 -translate-x-1/2 sm:block"
              style={{
                background: `linear-gradient(90deg, rgba(146,85,237,1) ${
                  Math.round(gradientPercent / 15) * 15
                }%, rgb(231,228,252,1) ${Math.round(gradientPercent / 15) * 15}%)`,
              }}
            />
            <div className="flex flex-col gap-2 sm:grid sm:grid-cols-3 sm:justify-between sm:gap-0">
              {steps.map((step, index) => (
                <Fragment key={`step-${index}`}>
                  {index > 0 ? (
                    <div className="flex h-16 justify-center sm:hidden">
                      <div
                        className="bg-primary-2-80 aria-disabled:bg-primary-2-20 h-full w-0.5"
                        aria-disabled={index > enabledStep}
                      />
                    </div>
                  ) : null}
                  <div className="flex flex-col items-center gap-4">
                    <div
                      className={cn(
                        'border-primary-2-10 aria-disabled:bg-primary-2-20 aria-disabled:text-primary-3-10 rounded-full border-8 p-6 aria-disabled:bg-none',
                        gradientStyle()
                      )}
                      aria-disabled={index > enabledStep}
                    >
                      {step.icon}
                    </div>
                    <div
                      className="font-heading-condensed aria-disabled:text-primary-3-10 text-xl leading-7 font-medium tracking-tight"
                      aria-disabled={index > enabledStep}
                    >
                      Step {index + 1}
                    </div>
                    <div
                      className="text-medium aria-disabled:text-primary-3-10 w-2/3 text-center leading-6"
                      aria-disabled={index > enabledStep}
                    >
                      {step.description}
                    </div>
                  </div>
                </Fragment>
              ))}
            </div>
          </div>
        </SectionWithHeading>
      </div>
    </>
  );
};

const Points = ({
  points,
}: {
  points: { icon: ReactNode; title: string; description: string }[];
}) => {
  return (
    <SectionWithHeading heading="Why us, you ask?">
      <div className="text-primary-2-80 relative mt-20 lg:w-4/5">
        <div className="grid grid-rows-3 justify-between gap-x-6 gap-y-12 md:grid-cols-3 md:grid-rows-1">
          {points.map((point, index) => (
            <div
              key={`point-${index}`}
              className={cn(
                'flex flex-col gap-4 p-6 sm:flex-row md:flex-col md:items-center',
                gradientStyle()
              )}
            >
              <div className="bg-primary-2-10 h-min w-min rounded-full p-4 shadow-sm">
                {point.icon}
              </div>
              <div className="text-left lg:text-center">
                <h3 className="font-heading-condensed text-xl leading-7 font-medium tracking-tight">
                  {point.title}
                </h3>
                <p className="text-neutral-2-80 text-base leading-5">{point.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionWithHeading>
  );
};

const FaqSection = ({ faqs }: { faqs: { question: string; answer: string; seo?: boolean }[] }) => {
  return (
    <SectionWithHeading heading="FAQs; Your questions, answered.">
      <FAQ faqs={faqs} />
    </SectionWithHeading>
  );
};

// The map card's picture shows places from the shopper's part of the world
const MAP_EXAMPLES: Record<string, { src: string; alt: string }> = {
  in: { src: mapPuzzleExample, alt: 'Map puzzles of India and of Mumbai' },
  ae: { src: mapPuzzleExampleAE, alt: 'Map puzzles of the UAE and of Dubai' },
  intl: { src: mapPuzzleExampleIntl, alt: 'Map puzzles of the USA and of Germany' },
};

// Puzzle Type Selector Component
const PuzzleTypeSelector = ({
  onSelectPhoto,
  onSelectMap,
  disabled,
  channelSlug,
}: {
  onSelectPhoto: () => void;
  onSelectMap: () => void;
  disabled?: boolean;
  channelSlug: string;
}) => {
  const mapExample = MAP_EXAMPLES[channelSlug] ?? MAP_EXAMPLES.in;
  return (
    <div className="mx-auto max-w-4xl px-4">
      <h2 className="font-heading-condensed text-primary-2-80 mb-8 text-center text-2xl font-medium sm:text-3xl">
        Choose Your Custom Puzzle Type
      </h2>
      <div className="grid gap-6 sm:grid-cols-2">
        {/* Photo Puzzle Option */}
        <button
          onClick={onSelectPhoto}
          disabled={disabled}
          className="group cursor-pointer rounded-2xl border-2 border-neutral-2-20 bg-white p-6 text-left transition-all hover:border-primary-2 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
        >
          <img
            src={imgUrl(image2, { width: 800 })}
            alt="A finished custom photo puzzle of a mountain lake"
            loading="lazy"
            className="mb-5 aspect-[7/5] w-full rounded-xl object-cover"
          />
          <h3 className="font-heading-condensed text-primary-2-80 mb-2 text-xl font-medium">
            Photo Puzzle
          </h3>
          <p className="text-neutral-2-60 text-sm">
            Upload your favorite photo and turn it into a custom jigsaw puzzle. Perfect for
            preserving memories and creating personalized gifts.
          </p>
          <div className="text-secondary-1 mt-4 text-sm font-medium group-hover:underline">
            Upload Your Photo →
          </div>
        </button>

        {/* Map Puzzle Option */}
        <button
          onClick={onSelectMap}
          disabled={disabled}
          className="group cursor-pointer rounded-2xl border-2 border-neutral-2-20 bg-white p-6 text-left transition-all hover:border-primary-2 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
        >
          <img
            src={imgUrl(mapExample.src, { width: 800 })}
            alt={mapExample.alt}
            loading="lazy"
            className="mb-5 aspect-[7/5] w-full rounded-xl object-cover"
          />
          <h3 className="font-heading-condensed text-primary-2-80 mb-2 text-xl font-medium">
            Map Puzzle
          </h3>
          <p className="text-neutral-2-60 text-sm">
            Transform any city, state or country, or the whole world, into a beautiful puzzle. Mark
            your own places with a heart and choose from 16 stunning color themes.
          </p>
          <div className="text-secondary-1 mt-4 text-sm font-medium group-hover:underline">
            Design Your Map →
          </div>
        </button>
      </div>
    </div>
  );
};

export default function Page({ params, loaderData }: Route.ComponentProps) {
  const { channel } = parseParams(params);
  const { slug } = schemaParams.parse(params);

  const { products, instock, originalUploadUrl, croppedUploadUrl } = loaderData;

  const selectedProduct = products.find((v) => v.slug === slug);

  const [showAlert, setShowAlert] = useState(false);
  const [showFlowSheet, setShowFlowSheet] = useState(false);
  const [showMapFlowSheet, setShowMapFlowSheet] = useState(false);

  // Flow to open once the dispatch notice is acknowledged.
  const [pendingFlow, setPendingFlow] = useState<'photo' | 'map' | null>(null);

  const name = selectedProduct?.name || 'Custom Puzzle';

  const startHref = selectedProduct
    ? `${urls.customPuzzle.step1(params)}?slug=${selectedProduct.slug}`
    : urls.customPuzzle.step1(params);

  const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
  const countryName = regionNames.of(channel.defaultCountry);
  const navigate = useNavigate();
  const [warning, setWarning] = useState(false);

  const steps = [
    {
      description: 'Select your ideal puzzle size and piece shape',
      icon: <PuzzlePieceIcon className="h-8 w-8" />,
    },
    {
      description: 'Upload your personalized photo',
      icon: <ArrowUpOnSquareStackIcon className="h-8 w-8" />,
    },
    {
      description: 'Define your preferred image area',
      icon: <PhotoIcon className="h-8 w-8" />,
    },
  ];

  const plusPoints = [
    {
      title: 'Mupltiple sizes',
      description:
        "Our selection includes a variety of puzzle sizes and piece counts, ensuring there's an ideal option for every puzzle enthusiast.",
      icon: <RectangleGroupIcon className="h-8 w-8" />,
    },
    {
      title: 'Prompt delivery',
      description: `We ship your ${name.toLowerCase()} within 3-4 business days.`,
      icon: <ClockIcon className="h-8 w-8" />,
    },
    {
      title: 'No minimum qty',
      description: `We proudly offer no minimum quantity, allowing you to order as few as one or as many as a hundred ${name.toLowerCase()}.`,
      icon: <ChartBarIcon className="h-8 w-8" />,
    },
  ];

  const slides = [<Slide index={0} key={0} params={params} name={name} startHref={startHref} />];

  const faqs = [
    {
      question: `Where can I buy a ${name.toLowerCase()} in ${countryName}?`,
      answer: `You've come to the right place! Jigsaw Nation is the best place to buy ${name.toLowerCase()} in ${countryName}. Immerse yourself in a unique puzzle experience by personalizing your favorite memories. From diverse sizes to intricate details, our custom photo puzzles are crafted to tell your story. Explore the possibilities and create your own masterpiece, available only at Jigsaw Nation. Start designing your personalized puzzle today!`,
      seo: true,
    },
    {
      question: `Is there an order minimum for ${name.toLowerCase()}?`,
      answer: `No, there is no order minimum for ${name.toLowerCase()}. Whether you're looking for a single personalized puzzle or a larger quantity, we welcome orders of any size, from 1 to 1000 or more. Feel free to order the quantity that suits your needs, and we'll ensure each custom puzzle is crafted with the same attention to detail and quality.`,
    },
    {
      question: `Do the ${name.toLowerCase()} come in a box?`,
      answer: `Yes, our ${name.toLowerCase()} are packaged in a specially designed, customized box created by us. This unique box complements the personalized nature of your puzzle, adding an extra touch to the overall presentation. However, if you prefer a different packaging option, you have the flexibility to opt out. In such cases, your custom puzzle will be securely delivered in a clear ziplock bag.`,
    },
    {
      question: `What is the recommended size my photo should be for each piece count?`,
      answer: `We offer a variety of piece counts to cater to your preferences. Our custom photo puzzles are available in several options, including 35 pieces, 50 pieces, 150 pieces, 280 pieces, and 500 pieces. Additionally, these puzzles come in various shapes, adding an extra layer of customization to your personalized puzzle experience.`,
    },
    {
      question: `Can I make changes after submitting my order?`,
      answer: `Once an order is submitted, changes cannot be made directly through the website. However, we understand that circumstances may arise, and we're here to help. If you need to make modifications to your order, please contact us promptly at hello@jigsawnation.com. Our team will do their best to accommodate your request and assist you with any adjustments within our capabilities.`,
    },
    {
      question: `Can I cancel my order?`,
      answer: `No, cancellation of orders is not possible through our website. Once an order is placed, it begins processing immediately to ensure prompt delivery. However, if you have concerns or specific reasons for wishing to cancel your order, please reach out to us at hello@jigsawnation.com. We understand that situations may arise, and we'll do our best to assist you and explore possible solutions based on your individual circumstances.`,
    },
    {
      question: `What should I do if something is wrong with my order when it arrives?`,
      answer: `Mistakes can happen, and we apologize for any inconvenience. Your satisfaction is important to us, and we will do our best to rectify any issues promptly. If you encounter any problems with your order, please reach out to us at hello@jigsawnation.com. Our customer support team is here to assist you and will work diligently to resolve the issue and ensure that you are satisfied with your experience.`,
    },
  ];

  const alertJokes = [
    "You clicked faster than our factory machines! Please make sure you've read everything before confirming.",
    "Was that a read or a reflex? Please make sure you've read everything before confirming.",
    "Hold on! The info's important — give it a quick read before continuing.",
  ];

  useEffect(() => {
    if (showAlert) {
      alert(alertJokes[Math.floor(Math.random() * alertJokes.length)]);

      setShowAlert(false);
    }
  }, [showAlert]);

  return (
    <>
      <CustomPuzzleFlowSheet
        isOpen={showFlowSheet}
        onClose={() => setShowFlowSheet(false)}
        products={products}
        params={{ locale: params.locale, channel: params.channel }}
        initialSlug={selectedProduct?.slug}
        originalUploadUrl={originalUploadUrl}
        croppedUploadUrl={croppedUploadUrl}
      />
      <MapPuzzleFlowSheet
        isOpen={showMapFlowSheet}
        onClose={() => setShowMapFlowSheet(false)}
        products={products}
        params={{ locale: params.locale, channel: params.channel }}
        initialSlug={selectedProduct?.slug}
      />

      <DispatchNoticeDialog
        isOpen={pendingFlow !== null}
        onClose={() => setPendingFlow(null)}
        onContinue={() => {
          if (pendingFlow === 'map') setShowMapFlowSheet(true);
          else setShowFlowSheet(true);
          setPendingFlow(null);
        }}
      />

      <main>
        {/* Puzzle Type Selector - Primary CTA */}
        <div className="py-16">
          <PuzzleTypeSelector
            onSelectPhoto={() => setPendingFlow('photo')}
            onSelectMap={() => setPendingFlow('map')}
            disabled={!instock}
            channelSlug={channel.urlSlug}
          />
        </div>

        <div className="pb-14">
          <Steps steps={steps} name={name.replace('Puzzle', 'Photo Puzzle')} />
        </div>
        <div className="-mx-6 my-14 h-[900px] lg:mx-0">
          <Hero
            heading="The perfect gift for anyone!"
            text={`Our personalized ${Pluralize(
              name.toLowerCase().replace('custom', 'photo'),
              2
            )} are the ideal gift for any occasion. Birthdays, anniversaries, or just because - these ${name} transform special memories into an unforgettable experience.`}
            image={{ src: heroCP, width: 1232, height: 900 }}
            cta={{
              to: startHref,
              text: 'Create Photo Puzzle',
              disabled: !instock,
              onClick: () => {
                setPendingFlow('photo');
              },
            }}
          />
          <Transition.Root show={warning} as={Fragment}>
            <Dialog
              as="div"
              className="fixed inset-0 z-10 overflow-y-auto"
              onClose={() => {
                setWarning(false);
                setShowAlert(false);
              }}
            >
              <Dialog.Overlay className="fixed inset-0 z-10 bg-black/50 backdrop-blur-sm" />
              <div className="flex min-h-screen items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <div className="bg-primary-2-10 relative z-20 inline-block w-full max-w-md transform overflow-hidden rounded-2xl p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title className="font-heading-condensed text-2xl leading-6 font-medium">
                      Heads Up: Processing Delays Going on Currently
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm font-semibold">
                        If you&apos;re in a hurry, please keep in mind that due to a{' '}
                        <span className="rounded px-1 font-extrabold text-red-700">
                          heavy workload at our factory (20-25 days delay)
                        </span>{' '}
                        Thank you for your patience and understanding.
                      </p>
                    </div>

                    <div className="mt-4 flex justify-end space-x-4">
                      <button
                        type="button"
                        data-testid="warning-button"
                        className={buttonVariants({ className: 'h-fit' })}
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition.Root>
        </div>
        <div className="py-14">
          <Points points={plusPoints} />
        </div>
        <div className="-mx-6 my-14 h-[900px] lg:mx-0">
          <Hero
            heading="Turn any city, state, country, or the whole world, into a puzzle"
            text="Create a stunning custom puzzle featuring the streets of your favorite city, a whole state or country, or the entire world, with your own places marked with a heart. Choose from 16 beautiful color themes to match your style."
            image={{ src: heroLP, width: 1280, height: 900 }}
            cta={{
              to: urls.customMapPuzzle.landing(params, {}),
              text: 'Create Map Puzzle',
              disabled: !instock,
              onClick: () => {
                setPendingFlow('map');
              },
            }}
            type={2}
            gradient={3}
          />
        </div>
        <div className="py-14">
          <FaqSection faqs={faqs} />
        </div>
      </main>
    </>
  );
}
